import React from 'react';
import { useTranslation } from 'react-i18next';
import { MainMatchesData } from './MainMatchesData';
import {
  FaFutbol,
  FaFlag,
  FaPercentage,
  FaArrowUp,
  FaArrowDown,
  FaExchangeAlt,
  FaHome,
  FaPlane,
  FaChartBar,
  FaShieldAlt,
  FaBullseye,
  FaBan,
  FaChartLine,
  FaTrophy,
  FaTable,
  FaThumbsUp,
  FaThumbsDown,
} from 'react-icons/fa';
import { BiFootball } from 'react-icons/bi';
import { AiOutlineFire } from 'react-icons/ai';
import { GiSoccerKick, GiSoccerField, GiSoccerBall } from 'react-icons/gi';
import { IoStatsChart } from 'react-icons/io5';
import { MdCategory } from 'react-icons/md';

interface Props {
  selectedItemData: MainMatchesData;
  columnToShow?: 'left' | 'center' | 'right' | 'all';
}

export const GoalsPrediction = ({
  selectedItemData,
  columnToShow = 'all',
}: Props) => {
  const { t } = useTranslation();

  if (!selectedItemData) {
    return <div>{t('goalsPrediction.noData')}</div>;
  }

  // Determine which columns to show
  const showLeft = columnToShow === 'left' || columnToShow === 'all';
  const showCenter = columnToShow === 'center' || columnToShow === 'all';
  const showRight = columnToShow === 'right' || columnToShow === 'all';

  const getCellColorClass = (value: number): string => {
    let probabilityClass = '';
    if (value <= 10) {
      probabilityClass = 'dark-red cell-text';
    } else if (value <= 20) {
      probabilityClass = 'medium-red cell-text';
    } else if (value <= 30) {
      probabilityClass = 'light-red cell-text';
    } else if (value <= 40) {
      probabilityClass = 'dark-orange cell-text';
    } else if (value <= 50) {
      probabilityClass = 'light-orange cell-text';
    } else if (value <= 60) {
      probabilityClass = 'dark-yellow cell-text';
    } else if (value <= 70) {
      probabilityClass = 'light-yellow cell-text';
    } else if (value <= 80) {
      probabilityClass = 'light-green cell-text';
    } else if (value <= 90) {
      probabilityClass = 'medium-green cell-text';
    } else {
      probabilityClass = 'dark-green cell-text';
    }
    return probabilityClass;
  };

  const getCellColorClassPossibleResult = (value: number): string => {
    let probabilityClass = '';
    if (value <= 4) {
      probabilityClass = 'dark-red cell-text';
    } else if (value <= 6) {
      probabilityClass = 'medium-red cell-text';
    } else if (value <= 7) {
      probabilityClass = 'dark-orange cell-text';
    } else if (value <= 9) {
      probabilityClass = 'light-yellow cell-text';
    } else if (value <= 15) {
      probabilityClass = 'light-green cell-text';
    } else if (value <= 25) {
      probabilityClass = 'medium-green cell-text';
    } else {
      probabilityClass = 'dark-green cell-text';
    }
    return probabilityClass;
  };

  const getCellColorClassGoalProb = (value: number): string => {
    let probabilityClass = '';
    if (value <= 7) {
      probabilityClass = 'medium-red cell-text';
    } else if (value < 12) {
      probabilityClass = 'dark-orange cell-text';
    } else if (value <= 19) {
      probabilityClass = 'light-yellow cell-text';
    } else if (value > 19 && value < 28) {
      probabilityClass = 'light-green cell-text';
    } else {
      probabilityClass = 'medium-green cell-text';
    }
    return probabilityClass;
  };

  const getHeaderStyle = (isHomeTeam: boolean) => {
    return isHomeTeam
      ? { background: 'linear-gradient(to right, #065f46, #064e3b)' }
      : { background: 'linear-gradient(to right, #1e40af, #1e3a8a)' };
  };

  // Componente per intestazioni con icone
  const HeaderWithIcon = ({
    icon: Icon,
    text,
    style = {},
  }: {
    icon: React.ElementType;
    text: string;
    style?: React.CSSProperties;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <Icon style={{ marginRight: '0.5rem' }} /> {text}
    </div>
  );

  // Componente per intestazioni di colonna con icone
  const ColumnHeaderWithIcon = ({
    icon: Icon,
    text,
  }: {
    icon: React.ElementType;
    text: string;
  }) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon style={{ marginRight: '0.25rem' }} /> {text}
    </span>
  );

  const formatNotAvailableValue = (value: number | null | undefined) => {
    return value === 0 || value === null || value === undefined ? 'N/A' : value;
  };

  return (
    <div className="TeamsAverageGoals">
      <div className="last5container">
        {/* COLONNA SINISTRA - HOME TEAM */}
        {showLeft && (
          <div className="left-table">
            {/* Tabella principale Over/Under e GG/NG */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={FaHome}
                        text={t('goalsPrediction.homeMatches')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 1.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 2.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 3.5`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {selectedItemData.HomeTeamHomeMatchesData.Over15}/
                      {selectedItemData.HomeTeamHomeMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.HomeTeamHomeMatchesData.Over25}/
                      {selectedItemData.HomeTeamHomeMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.HomeTeamHomeMatchesData.Over35}/
                      {selectedItemData.HomeTeamHomeMatchesData.TotalMatches}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.HomeTeamHomeMatchesData.Over15 /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.HomeTeamHomeMatchesData.Over15 /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.HomeTeamHomeMatchesData.Over25 /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.HomeTeamHomeMatchesData.Over25 /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.HomeTeamHomeMatchesData.Over35 /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.HomeTeamHomeMatchesData.Over35 /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaShieldAlt}
                        text={t('goalsPrediction.cleanSheet')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaExchangeAlt}
                        text={t('goalsPrediction.goalGoal')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaBan}
                        text={t('goalsPrediction.noGoal')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {selectedItemData.HomeTeamHomeMatchesData.CleanSheet}/
                      {selectedItemData.HomeTeamHomeMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.HomeTeamHomeMatchesData.GoalGoal}/
                      {selectedItemData.HomeTeamHomeMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.HomeTeamHomeMatchesData.NoGoal}/
                      {selectedItemData.HomeTeamHomeMatchesData.TotalMatches}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.HomeTeamHomeMatchesData.CleanSheet /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.HomeTeamHomeMatchesData.CleanSheet /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.HomeTeamHomeMatchesData.GoalGoal /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.HomeTeamHomeMatchesData.GoalGoal /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.HomeTeamHomeMatchesData.NoGoal /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.HomeTeamHomeMatchesData.NoGoal /
                          selectedItemData.HomeTeamHomeMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabella probabilità dei goal */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={2} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={GiSoccerBall}
                        text={t('goalsPrediction.homeGoalProbability')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaFutbol}
                        text={t('goalsPrediction.goals')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPercentage}
                        text={t('goalsPrediction.probability')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b>0</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.HomeTeamProbabilities.Goals.Goals_0,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Goals.Goals_0}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>1</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.HomeTeamProbabilities.Goals.Goals_1,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Goals.Goals_1}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>2</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.HomeTeamProbabilities.Goals.Goals_2,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Goals.Goals_2}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>3+</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.HomeTeamProbabilities.Goals.Goals_3 +
                          selectedItemData.HomeTeamProbabilities.Goals.Goals_4 +
                          selectedItemData.HomeTeamProbabilities.Goals.Goals_5,
                      )}
                    >
                      {Math.round(
                        selectedItemData.HomeTeamProbabilities.Goals.Goals_3 +
                          selectedItemData.HomeTeamProbabilities.Goals.Goals_4 +
                          selectedItemData.HomeTeamProbabilities.Goals.Goals_5,
                      )}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabella over/under squadra */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={4} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={FaChartLine}
                        text={t('goalsPrediction.homeOverUnder')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>{t('goalsPrediction.line')}</th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} %`}
                      />
                    </th>
                    <th>{t('goalsPrediction.line')}</th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowDown}
                        text={`${t('goalsPrediction.under')} %`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b>0.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.HomeTeamProbabilities.Over.Over_05,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Over.Over_05}%
                    </td>
                    <td>
                      <b>0.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.HomeTeamProbabilities.Under.Under_05,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Under.Under_05}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>1.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.HomeTeamProbabilities.Over.Over_15,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Over.Over_15}%
                    </td>
                    <td>
                      <b>1.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.HomeTeamProbabilities.Under.Under_15,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Under.Under_15}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>2.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.HomeTeamProbabilities.Over.Over_25,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Over.Over_25}%
                    </td>
                    <td>
                      <b>2.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.HomeTeamProbabilities.Under.Under_25,
                      )}
                    >
                      {selectedItemData.HomeTeamProbabilities.Under.Under_25}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabella tiri - informazioni essenziali */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={GiSoccerKick}
                        text={t('goalsPrediction.shotsPerMatch')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalsPrediction.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsUp}
                        text={t('goalsPrediction.for')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsDown}
                        text={t('goalsPrediction.against')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaBullseye style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalsPrediction.onTarget')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeShots.OnTargetAverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeShots
                          .OnTargetVsAverageAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalsPrediction.total')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeShots.AverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeShots.AverageVsAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* COLONNA CENTRALE */}
        {showCenter && (
          <div className="center-table">
            {/* Expected Goals */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      colSpan={2}
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    >
                      <HeaderWithIcon
                        icon={BiFootball}
                        text={t('goalsPrediction.expectedGoals')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaHome}
                        text={t('goalsPrediction.home')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPlane}
                        text={t('goalsPrediction.away')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <div className="biggerFont">
                        {selectedItemData.MatchProbabilities.ExpectedGoalHome}
                      </div>
                    </td>
                    <td>
                      <div className="biggerFont">
                        {selectedItemData.MatchProbabilities.ExpectedGoalAway}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Probabilità Goal/No Goal */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      colSpan={2}
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    >
                      <HeaderWithIcon
                        icon={IoStatsChart}
                        text={t('goalsPrediction.probability')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaExchangeAlt}
                        text={t('goalsPrediction.goalGoal')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaBan}
                        text={t('goalsPrediction.noGoal')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={`${getCellColorClass(selectedItemData.MatchProbabilities.GoalGoal)} biggerFont`}
                    >
                      {selectedItemData.MatchProbabilities.GoalGoal}%
                    </td>
                    <td
                      className={`${getCellColorClass(selectedItemData.MatchProbabilities.NoGoal)} biggerFont`}
                    >
                      {selectedItemData.MatchProbabilities.NoGoal}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Probabilità Over */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      colSpan={3}
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    >
                      <HeaderWithIcon
                        icon={FaArrowUp}
                        text={t('goalsPrediction.overProbability')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 1.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 2.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 3.5`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={getCellColorClass(
                        selectedItemData.MatchProbabilities.Over.Over_15,
                      )}
                    >
                      {selectedItemData.MatchProbabilities.Over.Over_15}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.MatchProbabilities.Over.Over_25,
                      )}
                    >
                      {selectedItemData.MatchProbabilities.Over.Over_25}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.MatchProbabilities.Over.Over_35,
                      )}
                    >
                      {selectedItemData.MatchProbabilities.Over.Over_35}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Probabilità Under */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      colSpan={3}
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    >
                      <HeaderWithIcon
                        icon={FaArrowDown}
                        text={t('goalsPrediction.underProbability')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowDown}
                        text={`${t('goalsPrediction.under')} 1.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowDown}
                        text={`${t('goalsPrediction.under')} 2.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowDown}
                        text={`${t('goalsPrediction.under')} 3`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={getCellColorClass(
                        selectedItemData.MatchProbabilities.Under.Under_15,
                      )}
                    >
                      {selectedItemData.MatchProbabilities.Under.Under_15}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.MatchProbabilities.Under.Under_25,
                      )}
                    >
                      {selectedItemData.MatchProbabilities.Under.Under_25}%
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.MatchProbabilities.Under.Under_35,
                      )}
                    >
                      {selectedItemData.MatchProbabilities.Under.Under_35}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Top 7 risultati esatti più probabili */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th
                      colSpan={4}
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    >
                      <HeaderWithIcon
                        icon={FaTrophy}
                        text={t('goalsPrediction.probableExactScores')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon icon={FaHome} text="" />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon icon={FaPlane} text="" />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPercentage}
                        text={t('goalsPrediction.probability')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={AiOutlineFire}
                        text={t('goalsPrediction.odds')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemData.MatchProbabilities.PossibleResults.sort(
                    (a, b) => b.Probability - a.Probability,
                  )
                    .slice(0, 7)
                    .map((match, index) => (
                      <tr key={index}>
                        <td>{match.Home}</td>
                        <td>{match.Away}</td>
                        <td
                          className={getCellColorClassPossibleResult(
                            match.Probability,
                          )}
                        >
                          {match.Probability}%
                        </td>
                        <td>{match.Odd}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* COLONNA DESTRA - AWAY TEAM */}
        {showRight && (
          <div className="right-table">
            {/* Tabella principale Over/Under e GG/NG */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={FaPlane}
                        text={t('goalsPrediction.awayMatches')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 1.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 2.5`}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} 3.5`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {selectedItemData.AwayTeamAwayMatchesData.Over15}/
                      {selectedItemData.AwayTeamAwayMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.AwayTeamAwayMatchesData.Over25}/
                      {selectedItemData.AwayTeamAwayMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.AwayTeamAwayMatchesData.Over35}/
                      {selectedItemData.AwayTeamAwayMatchesData.TotalMatches}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.AwayTeamAwayMatchesData.Over15 /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.AwayTeamAwayMatchesData.Over15 /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.AwayTeamAwayMatchesData.Over25 /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.AwayTeamAwayMatchesData.Over25 /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.AwayTeamAwayMatchesData.Over35 /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.AwayTeamAwayMatchesData.Over35 /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                  </tr>
                </tbody>
                <thead>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaShieldAlt}
                        text={t('goalsPrediction.cleanSheet')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaExchangeAlt}
                        text={t('goalsPrediction.goalGoal')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaBan}
                        text={t('goalsPrediction.noGoal')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {selectedItemData.AwayTeamAwayMatchesData.CleanSheet}/
                      {selectedItemData.AwayTeamAwayMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.AwayTeamAwayMatchesData.GoalGoal}/
                      {selectedItemData.AwayTeamAwayMatchesData.TotalMatches}
                    </td>
                    <td>
                      {selectedItemData.AwayTeamAwayMatchesData.NoGoal}/
                      {selectedItemData.AwayTeamAwayMatchesData.TotalMatches}
                    </td>
                  </tr>
                  <tr>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.AwayTeamAwayMatchesData.CleanSheet /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.AwayTeamAwayMatchesData.CleanSheet /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.AwayTeamAwayMatchesData.GoalGoal /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.AwayTeamAwayMatchesData.GoalGoal /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                    <td
                      className={getCellColorClass(
                        (selectedItemData.AwayTeamAwayMatchesData.NoGoal /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                    >
                      {Math.floor(
                        (selectedItemData.AwayTeamAwayMatchesData.NoGoal /
                          selectedItemData.AwayTeamAwayMatchesData
                            .TotalMatches) *
                          100,
                      )}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabella probabilità dei goal */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={2} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={GiSoccerBall}
                        text={t('goalsPrediction.awayGoalProbability')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaFutbol}
                        text={t('goalsPrediction.goals')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaPercentage}
                        text={t('goalsPrediction.probability')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b>0</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.AwayTeamProbabilities.Goals.Goals_0,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Goals.Goals_0}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>1</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.AwayTeamProbabilities.Goals.Goals_1,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Goals.Goals_1}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>2</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.AwayTeamProbabilities.Goals.Goals_2,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Goals.Goals_2}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>3+</b>
                    </td>
                    <td
                      className={getCellColorClassGoalProb(
                        selectedItemData.AwayTeamProbabilities.Goals.Goals_3 +
                          selectedItemData.AwayTeamProbabilities.Goals.Goals_4 +
                          selectedItemData.AwayTeamProbabilities.Goals.Goals_5,
                      )}
                    >
                      {Math.round(
                        selectedItemData.AwayTeamProbabilities.Goals.Goals_3 +
                          selectedItemData.AwayTeamProbabilities.Goals.Goals_4 +
                          selectedItemData.AwayTeamProbabilities.Goals.Goals_5,
                      )}
                      %
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabella over/under squadra */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={4} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={FaChartLine}
                        text={t('goalsPrediction.awayOverUnder')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>{t('goalsPrediction.line')}</th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowUp}
                        text={`${t('goalsPrediction.over')} %`}
                      />
                    </th>
                    <th>{t('goalsPrediction.line')}</th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaArrowDown}
                        text={`${t('goalsPrediction.under')} %`}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <b>0.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.AwayTeamProbabilities.Over.Over_05,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Over.Over_05}%
                    </td>
                    <td>
                      <b>0.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.AwayTeamProbabilities.Under.Under_05,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Under.Under_05}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>1.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.AwayTeamProbabilities.Over.Over_15,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Over.Over_15}%
                    </td>
                    <td>
                      <b>1.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.AwayTeamProbabilities.Under.Under_15,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Under.Under_15}%
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <b>2.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.AwayTeamProbabilities.Over.Over_25,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Over.Over_25}%
                    </td>
                    <td>
                      <b>2.5</b>
                    </td>
                    <td
                      className={getCellColorClass(
                        selectedItemData.AwayTeamProbabilities.Under.Under_25,
                      )}
                    >
                      {selectedItemData.AwayTeamProbabilities.Under.Under_25}%
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tabella tiri - informazioni essenziali */}
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={GiSoccerKick}
                        text={t('goalsPrediction.shotsPerMatch')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalsPrediction.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsUp}
                        text={t('goalsPrediction.for')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsDown}
                        text={t('goalsPrediction.against')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaBullseye style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalsPrediction.onTarget')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayShots.OnTargetAverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayShots
                          .OnTargetVsAverageAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />
                        {t('goalsPrediction.total')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayShots.AverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayShots.AverageVsAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
